/**
 * Custom hook that detects clicks/taps on data-ai-component annotated elements
 * within a container and resolves them to structured game components.
 *
 * Applies a visual highlight CSS class to the clicked element that persists
 * until clearSelection is called.
 *
 * Returns:
 *   - selectedText: the text content of the clicked element
 *   - selection: structured component object from data-ai-* attributes, or null
 *   - clearSelection: function to clear the highlight and reset state
 */

import { useState, useEffect, useCallback, useRef } from 'react';

const HIGHLIGHT_CLASS = 'ai-helper-highlight';

/**
 * Walk up from the clicked element to find the nearest [data-ai-component]
 * ancestor. Extract the component type and all data-ai-* params.
 */
function resolveComponent(el) {
  if (!el) return null;

  const annotated = el.closest?.('[data-ai-component]');
  if (!annotated) return null;

  const component = annotated.dataset.aiComponent;
  const params = {};

  for (const [key, value] of Object.entries(annotated.dataset)) {
    // dataset keys are camelCased: data-ai-group-index → aiGroupIndex
    if (key.startsWith('ai') && key !== 'aiComponent') {
      // Convert aiGroupIndex → groupIndex
      const paramName = key
        .replace(/^ai/, '')
        .replace(/^./, (c) => c.toLowerCase());
      params[paramName] = value;
    }
  }

  return { element: annotated, component, ...params };
}

export function useClickSelection(containerRef, enabled = true) {
  const [selectedText, setSelectedText] = useState('');
  const [selection, setSelection] = useState(null);
  const highlightedElRef = useRef(null);

  const clearSelection = useCallback(() => {
    if (highlightedElRef.current) {
      highlightedElRef.current.classList.remove(HIGHLIGHT_CLASS);
      highlightedElRef.current = null;
    }
    setSelectedText('');
    setSelection(null);
  }, []);

  useEffect(() => {
    if (!enabled) {
      clearSelection();
      return;
    }

    const container = containerRef.current;
    if (!container) return;

    const handleClick = (e) => {
      // If the user was dragging to select text, don't trigger AI helper
      const currentSel = document.getSelection();
      if (currentSel && currentSel.toString().trim().length > 0) {
        return;
      }

      const target = e.target;

      // Resolve to a structured game component
      const result = resolveComponent(target);

      if (!result) {
        // Clicked outside any annotated element — ignore
        return;
      }

      const { element, ...componentData } = result;
      const shouldElementBeHighlighted = element.dataset.aiSkipHighlight !== 'true';

      // Clear previous highlight
      if (highlightedElRef.current && highlightedElRef.current !== element) {
        highlightedElRef.current.classList.remove(HIGHLIGHT_CLASS);
      }

      // Apply highlight to the clicked element unless explicitly disabled.
      if (shouldElementBeHighlighted) {
        element.classList.add(HIGHLIGHT_CLASS);
        highlightedElRef.current = element;
      } else if (highlightedElRef.current === element) {
        highlightedElRef.current.classList.remove(HIGHLIGHT_CLASS);
        highlightedElRef.current = null;
      }

      // Extract text and selection data
      // Prefer explicit data-ai-word over DOM textContent (which may be uppercased for display)
      const text = element.dataset.aiWord || element.textContent?.trim() || '';
      setSelectedText(text);
      setSelection(componentData);
    };

    container.addEventListener('click', handleClick);

    return () => {
      container.removeEventListener('click', handleClick);
    };
  }, [enabled, containerRef, clearSelection]);

  // Clean up highlight on unmount
  useEffect(() => {
    return () => {
      if (highlightedElRef.current) {
        highlightedElRef.current.classList.remove(HIGHLIGHT_CLASS);
        highlightedElRef.current = null;
      }
    };
  }, []);

  return { selectedText, selection, clearSelection };
}
